import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpErrorHandlerService, HandleError } from '../shared/http-error-handler.service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ForuscareService } from '../foruscare/foruscare.service';
import { Account } from '../../models/account';
import { serviceWorkflow } from '../../models/service';
import { MRNConfig } from 'src/app/models/mrnConfig';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private handleError: HandleError;
  constructor(
    public http: HttpClient,
    public fourscareService: ForuscareService,
    httpErrorHandler: HttpErrorHandlerService) {
    this.handleError = httpErrorHandler.createHandleError('Account Service');
  }

  createAccount(account: Account): Observable<any> {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/accounts", account)
      .pipe(
        catchError(this.handleError('account creation'))
      );
  }

  updateAccount(account: Account): Observable<any> {
    return this.http.put<any>(this.fourscareService.baseAuthURL() + "/accounts/" + account.id, account)
      .pipe(
        catchError(this.handleError('account updation'))
      );
  }

  getExistingAccounts(): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accounts/getAllAccountsBasicInfo")
      .pipe(
        catchError(this.handleError('listing existing accounts'))
      );
  }
  getProvidersBasicInfo(): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accounts/getAllAccountsBasicInfo")
      .pipe(
        catchError(this.handleError('listing existing accounts'))
      );
  }

  getExistingAccountsPaging(count, offset): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accounts?count=" + count + "&offset=" + offset)
      .pipe(
        catchError(this.handleError('listing existing accounts'))
      );
  }

  getDeviceCategories(): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/admin/device/getAllCategorys")
      .pipe(
        catchError(this.handleError('Error fetching device categories'))
      );
  }

  getAccountWorkflowByAccountId(accountId): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accountWorkFlow/" + accountId)
      .pipe(
        catchError(this.handleError('account workflow by account id'))
      );
  }

  addAccountWorkflow(workflow: serviceWorkflow[]): Observable<any> {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/accountWorkFlow", workflow)
      .pipe(
        catchError(this.handleError('account workflow creation'))
      );
  }


  updateAccountWorkflow(workflow: serviceWorkflow[], accountId): Observable<any> {
    return this.http.put<any>(this.fourscareService.baseAuthURL() + "/accountWorkFlow/" + accountId, workflow)
      .pipe(
        catchError(this.handleError('account workflow updation'))
      );
  }

  getAccountInfoById(accountId: string): Observable<any> {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accounts/" + accountId)
      .pipe(
        catchError(this.handleError('listing existing accounts'))
      );
  }

  getIncentiveConfigByAccountId(accountId: string): Observable<any> {
    return this.http.get<any>(this.fourscareService.hostUrl + "/EssilorIncentive/api/accountIntensive/account/" + accountId)
      .pipe(
        catchError(this.handleError('incentive config by account id'))
      );
  }

  addOrUpdateIncentiveConfig(incentiveConfig: any): Observable<any> {
    return this.http.post<any>(this.fourscareService.hostUrl + "/EssilorIncentive/api/accountIntensive/save/", incentiveConfig)
      .pipe(
        catchError(this.handleError('add or update incentive config'))
      );
  }
  getUserTypeIncentiveConfigByUserType(userType: string): Observable<any> {
    return this.http.get<any>(this.fourscareService.hostUrl + "/EssilorIncentive/api/userType/getUserTypeIncentiveConfigByUserType/" + userType)
      .pipe(
        catchError(this.handleError('incentive config by user type'))
      );
  }

  addOrUpdateUserTypeIncentiveConfig(incentiveConfig: any): Observable<any> {
    return this.http.post<any>(this.fourscareService.hostUrl + "/EssilorIncentive/api/userType/addUserTypeIncentiveConfig", incentiveConfig)
      .pipe(
        catchError(this.handleError('add or update user type incentive config'))
      );
  }
  deleteAccount(accountId: string): Observable<any> {
    return this.http.delete<any>(this.fourscareService.baseAuthURL() + "/oauth/token")
      .pipe(
        catchError(this.handleError("delete account by id"))
      );
  }

  uploadAccountLogo(formData: any, accountId: any): Observable<any> {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/accounts/uploadLogo/" + accountId, formData)
      .pipe(
        catchError(this.handleError("account logo upload"))
      );
  }

  getTimeZoneList() {
    return this.http.get<any>('assets/lib/timezones.json')
      .pipe(
        catchError(this.handleError("loading timezone"))
      );
  }

  getLanguageCodes() {
    return this.http.get<any>('assets/lib/language-codes_json.json')
      .pipe(
        catchError(this.handleError("loading languages"))
      );
  }
  getLanguagesList() {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + '/language/getAllLanguages')
      .pipe(
        catchError(this.handleError("loading languages"))
      );
  }

  screeningCentersByAccount(accountId): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/admin/organization/getScreeningCentersList/" + accountId)
      .pipe(
        catchError(this.handleError("screening center list by account id"))
      );
  }
  screeningCentersByAccountAndDeviceCategory(payload): any {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/admin/organization/getUnassignedCenters/deviceCategory", payload)
      .pipe(
        catchError(this.handleError("screening center list by account id"))
      );
  }
  getCMCentersByUserId(userId): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/admin/organization/getCMCentersByUserId/" + userId)
      .pipe(
        catchError(this.handleError("screening center list by user id"))
      );
  }
  insertDefaultFields(accountId, customFieldConfig) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/customFields/addOrUpdateCustomFields" + "/" + accountId, customFieldConfig)
      .pipe(
        catchError(this.handleError('Error While Updating Custom Field Data'))
      );
  }
  getRefractionConfigByAccount(accountId): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/refraction/configuration/" + accountId)
      .pipe(
        catchError(this.handleError("error in fetching refraction config by account id"))
      );
  }
  exportIncentiveReportXLS(payload) {
    return this.http.post(this.fourscareService.basehostUrl() + "/EssilorIncentive/api/IncentiveTransaction/exportTransactions/eyemitra", payload, { observe: 'response', responseType: 'blob' }) as any
  }
  getMRNConfigByAccount(accountId): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/mrnConfig/get/" + accountId)
      .pipe(
        catchError(this.handleError("mrn config by account id"))
      );
  }
  getAccountConfigurationKeys(): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accountconfig/getConfiguration")
      .pipe(
        catchError(this.handleError("Unable to fetch account configuration"))
      );
  }
  getAccountConfigurationByAccountId(obj): any {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/accountconfig/getAccountConfigurationByAccountId", obj)
      .pipe(
        catchError(this.handleError("Unable to fetch account configuration"))
      );
  }
  addMRNConfigByAccount(obj: MRNConfig) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/mrnConfig/add", obj)
      .pipe(
        catchError(this.handleError('Error While adding mrn config'))
      );
  }
  removeMRNConfigByAccount(accountId): any {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/mrnConfig/delete/" + accountId, {})
      .pipe(
        catchError(this.handleError('Error While adding mrn config'))
      );
  }
  addUpdateAccountConfig(reqObj): any {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/accountconfig/addUpdateConfiguration", reqObj)
      .pipe(
        catchError(this.handleError('Error While adding mrn config'))
      );
  }
  updateMRNConfigByAccount(obj: MRNConfig) {
    return this.http.put<any>(this.fourscareService.baseAuthURL() + "/mrnConfig/update", obj)
      .pipe(
        catchError(this.handleError('Error While updating mrn config'))
      );
  }

  getAdditionalServices(): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/accountWorkFlow/additional/platform/services")
      .pipe(
        catchError(this.handleError("Unable to fetch additional services"))
      );
  }
  getNotificationEventList(): any {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/notification/getEventsList")
      .pipe(
        catchError(this.handleError("notification event list"))
      );
  }

  getScreeningEvents(payload: object, limit: number, offset: number) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/screeningevent/getScreeningEventConfig?limit=" + limit + "&offset=" + offset, payload)
      .pipe(
        catchError(this.handleError("Screening Events Config"))
      );
  }

  generateScreeeningEventInviteLink(payload: object) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/screeningevent/generateInviteLink", payload)
      .pipe(
        catchError(this.handleError("Screening Events InviteLink failed"))
      );
  }
  createOrUpdateScreeningEvent(payload: object) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/screeningevent/updateScreeningEventConfig", payload)
      .pipe(
        catchError(this.handleError("Screening Events creation and updation failed"))
      );
  }

  enableDisableEvent(payload: object) {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/screeningevent/deactivateEvent", payload)
      .pipe(
        catchError(this.handleError("Screening Events creation and updation failed"))
      );
  }

  getAllServiceProvidersByFilter(payload: object): Observable<any> {
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/serviceProvider/getAllServiceProvidersByFilter", payload)
      .pipe(
        catchError(this.handleError("Failed to get service provider list"))
      );
  }

  getRenewalLinkforPackage(packageId: any) {
    return this.http.get<any>(this.fourscareService.baseAuthURL() + "/organization/purchase/reportPackage/generate/renewalRechargeLink/" + packageId)
      .pipe(
        catchError(this.handleError("Failed to get package renewal link."))
      );
  }

  generateExcelForImport(accountId: string, payload:any){
    return this.http.post<any>(this.fourscareService.baseAuthURL() + "/excel/generate/"+accountId, payload)
      .pipe(
        catchError(this.handleError("Failed to get service provider list"))
      );
  }
}
